import { usePersonnel } from 'features/Responder/features/Personnel';
import {
  getRunStatusLabel,
  resolvePersonnelName,
  resolveRunUserId,
} from 'features/Responder/features/Schedules/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Stack } from 'react-bootstrap';
import { FaHistory } from 'react-icons/fa';
import 'features/Responder/styles/responder-interactions.css';
import { formatEventTimestamp, getEventKindDefinition } from './eventHistoryUtils';
import './eventHistory.css';

const formatRunTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

export default function RunEventHistoryPanel({
  schedule = null,
  run = null,
  events = [],
  loading = false,
  error = null,
  selectedEventId = null,
  onSelectEvent,
  onRefresh,
}) {
  const { personnel } = usePersonnel();
  const listRef = useRef(null);
  const itemRefs = useRef(new Map());
  const seenEventIdsRef = useRef(new Set());
  const hasInitializedEventsRef = useRef(false);
  const [freshEventIds, setFreshEventIds] = useState(new Set());

  const sortedEvents = useMemo(() => (Array.isArray(events) ? events : []), [events]);

  useEffect(() => {
    if (!sortedEvents.length) {
      seenEventIdsRef.current = new Set();
      hasInitializedEventsRef.current = false;
      setFreshEventIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const ids = sortedEvents.map((event) => `${event.id}`);
    if (!hasInitializedEventsRef.current) {
      seenEventIdsRef.current = new Set(ids);
      hasInitializedEventsRef.current = true;
      return;
    }

    const newlyAdded = ids.filter((id) => !seenEventIdsRef.current.has(id));
    seenEventIdsRef.current = new Set(ids);
    if (!newlyAdded.length) return;

    setFreshEventIds((prev) => {
      const next = new Set(prev);
      newlyAdded.forEach((id) => next.add(id));
      return next;
    });

    const timeoutId = setTimeout(() => {
      setFreshEventIds((prev) => {
        const next = new Set(prev);
        newlyAdded.forEach((id) => next.delete(id));
        return next;
      });
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [sortedEvents]);

  useEffect(() => {
    if (!selectedEventId) return;
    const target = itemRefs.current.get(`${selectedEventId}`);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedEventId]);

  const scheduleLabel = schedule?.name || (schedule?.id ? `Schedule #${schedule.id}` : 'Schedule');
  const runUserId = resolveRunUserId(run);
  const runOwnerName = resolvePersonnelName(personnel, runUserId);
  const headerLabel =
    runOwnerName || (runUserId != null ? `User #${runUserId}` : run ? 'Activity' : 'No activity selected');
  const statusLabel = getRunStatusLabel(run);

  return (
    <Card className="shadow-sm h-100 event-history-panel bg-dark text-white border-secondary">
      <Card.Body className="d-flex flex-column gap-3 h-100">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted text-uppercase small">Run Event History</div>
            <div className="fw-semibold fs-5">{headerLabel}</div>
            <div className="small text-muted">{scheduleLabel}</div>
          </div>
          <Badge bg={statusLabel === 'active' ? 'success' : 'secondary'} className="text-uppercase">
            {statusLabel}
          </Badge>
        </div>

        <div className="small text-muted">
          <div>
            <strong>Started:</strong> {formatRunTime(run?.started_at || run?.startedAt)}
          </div>
          <div>
            <strong>Ended:</strong> {formatRunTime(run?.ended_at || run?.endedAt)}
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <div className="small text-muted">
            {loading
              ? 'Loading event history...'
              : error
                ? 'Unable to load event history.'
                : `${sortedEvents.length} events`}
          </div>
          {onRefresh && (
            <Button size="sm" variant="outline-primary" onClick={onRefresh}>
              Refresh
            </Button>
          )}
        </div>

        <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }} ref={listRef}>
          {sortedEvents.length ? (
            <Stack gap={2}>
              {sortedEvents.map((event) => {
                const definition = getEventKindDefinition(event.kind);
                const Icon = definition.Icon;
                const isSelected = `${event.id}` === `${selectedEventId}`;
                const isFresh = freshEventIds.has(`${event.id}`);
                return (
                  <div
                    key={`event-${event.id}`}
                    className={`event-history-item ${isSelected ? 'is-selected' : ''} ${isFresh ? 'is-fresh' : ''}`}
                    ref={(el) => {
                      if (el) itemRefs.current.set(`${event.id}`, el);
                      else itemRefs.current.delete(`${event.id}`);
                    }}
                    onClick={() => onSelectEvent?.(event, 'list')}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <span className="event-history-icon" style={{ background: definition.color }}>
                        {Icon && <Icon size={14} />}
                      </span>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="fw-semibold">{event.label || definition.label}</div>
                          <Badge bg="secondary" className="text-uppercase">
                            {definition.label}
                          </Badge>
                        </div>
                        {event.timestamp && (
                          <div className="small event-history-meta">
                            {formatEventTimestamp(event.timestamp)}
                          </div>
                        )}
                        {event.details && (
                          <div className="small text-muted mt-1">{event.details}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </Stack>
          ) : (
            <div className="d-flex align-items-center gap-2 text-muted">
              <FaHistory />
              <span>
                {loading
                  ? 'Loading event history...'
                  : 'There is no event history to display for this run.'}
              </span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
